import { useCallback, useEffect, useRef, useState } from 'react'

interface CaseStudy {
	id: string
	title: string
	slug: string
	client?: string | null
	imageUrl?: string | null
	imageAlt?: string | null
	videoMode?: 'none' | 'hover' | 'autoplay'
	videoPlaybackId?: string
	featured?: boolean
	order?: number
	services?: Array<{ id: string; name: string; slug: string }>
	sectors?: Array<{ id: string; name: string; slug: string }>
}

interface LazyLoadResponse {
	items: CaseStudy[]
	nextCursor: string | null
	hasMore: boolean
}

interface UseLazyCasesOptions {
	initialCases: CaseStudy[]
	serviceSlug?: string | null
	sectorSlug?: string | null
	enabled?: boolean
}

/**
 * Hook for lazy loading cases with infinite scroll support.
 * Uses cursor-based pagination for stable ordering.
 * Uses plain fetch() instead of useFetcher to avoid infinite render loops
 * (useFetcher object reference changes every render in React Router v7).
 */
export function useLazyCases({
	initialCases,
	serviceSlug,
	sectorSlug,
	enabled = true,
}: UseLazyCasesOptions) {
	const [cases, setCases] = useState<CaseStudy[]>(initialCases)
	const [cursor, setCursor] = useState<string | null>(
		initialCases.length > 0
			? initialCases[initialCases.length - 1]?.id ?? null
			: null,
	)
	const [hasMore, setHasMore] = useState(true)
	const [isLoading, setIsLoading] = useState(false)
	const observerRef = useRef<HTMLDivElement | null>(null)
	// Track previous initialCases by comparing IDs to avoid re-running on
	// every render when the parent passes a new array reference
	const prevInitialIdsRef = useRef<string>('')
	const prevServiceSlugRef = useRef<string | undefined | null>(serviceSlug)
	const prevSectorSlugRef = useRef<string | undefined | null>(sectorSlug)

	// Reset when initial data or filter changes
	useEffect(() => {
		const ids = initialCases.map((c) => c.id).join(',')
		if (
			ids !== prevInitialIdsRef.current ||
			serviceSlug !== prevServiceSlugRef.current ||
			sectorSlug !== prevSectorSlugRef.current
		) {
			prevInitialIdsRef.current = ids
			prevServiceSlugRef.current = serviceSlug
			prevSectorSlugRef.current = sectorSlug
			setCases(initialCases)
			setCursor(
				initialCases.length > 0
					? initialCases[initialCases.length - 1]?.id ?? null
					: null,
			)
			setHasMore(initialCases.length > 0)
		}
	}, [initialCases, serviceSlug, sectorSlug])

	// Load more cases using plain fetch
	const loadMore = useCallback(() => {
		if (isLoading || !hasMore || !enabled || !cursor) return

		setIsLoading(true)
		const params = new URLSearchParams()
		params.set('cursor', cursor)
		if (serviceSlug) {
			params.set('service', serviceSlug)
		}
		if (sectorSlug) {
			params.set('sector', sectorSlug)
		}

		fetch(`/resources/cases-public?${params.toString()}`)
			.then((res) => res.json() as Promise<LazyLoadResponse>)
			.then((data) => {
				const { items, nextCursor, hasMore: more } = data
				if (items.length > 0) {
					setCases((prev) => {
						// Deduplicate in case of race conditions
						const existingIds = new Set(prev.map((c) => c.id))
						const newItems = items.filter(
							(item) => !existingIds.has(item.id),
						)
						return [...prev, ...newItems]
					})
					setCursor(nextCursor)
				}
				setHasMore(more)
				setIsLoading(false)
			})
			.catch(() => {
				setIsLoading(false)
			})
	}, [cursor, hasMore, isLoading, enabled, serviceSlug, sectorSlug])

	// Set up intersection observer for infinite scroll
	useEffect(() => {
		if (!enabled || !hasMore) return

		const currentRef = observerRef.current
		if (!currentRef) return

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) {
					loadMore()
				}
			},
			{ rootMargin: '200px' },
		)

		observer.observe(currentRef)

		return () => {
			observer.unobserve(currentRef)
			observer.disconnect()
		}
	}, [enabled, hasMore, loadMore])

	return {
		cases,
		loadMore,
		hasMore,
		isLoading,
		observerRef,
	}
}
